import { useState } from "react";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { getYearSelectOptions } from "./utils";
import styled from "@emotion/styled";
import { Typography } from "../Typography";
import { FormSection } from "../FormSection";
import {
  InputVariantTypes,
  User,
  ImpactOrderReportPeriod,
  IMPACT_ORDER_SUPPORTED_FROM_YEAR,
} from "@ecologi/shared";

import { Select } from "../Select";
import { APIUsageGraph } from "./APIHistoryGraph";
import { Spinner } from "../Spinner";

const SelectContainer = styled.div`
  width: 136px;
  @media ${(props) => props.theme.bp.mobileDown} {
    width: 100%;
    margin-bottom: 4px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

const ChartCaption = styled(Typography)`
  margin-top: 20px;
`;

const StyledUsageSection = styled(FormSection)`
  background-color: ${({ theme }) => theme.colors.mushroomLight};
  border-radius: 20px;
  padding: 56px;
  margin-top: 64px;
  @media ${(props) => props.theme.bp.mobileDown} {
    padding: 24px;
    margin-top: 24px;
  }
`;

export const APIUsageHistory = ({
  user,
  getApiHistoryForYear,
}: {
  user: User;
  getApiHistoryForYear: (
    year: string
  ) => Promise<{ periods: ImpactOrderReportPeriod[] }>;
}) => {
  const [year, setYear] = useState<string>(DateTime.local().year.toString());

  const { data, isLoading: isApiHistoryLoading } = useQuery(
    ["api-history", year],
    () => getApiHistoryForYear(year),
    {
      onError: () => {
        toast.error(
          "There was an issue fetching your API usage history. Please try refreshing the page."
        );
      },
    }
  );

  return (
    <StyledUsageSection
      title="Usage history"
      rightTitleSlot={
        <SelectContainer>
          <Select
            variant={InputVariantTypes.SNOWDROP}
            hiddenLabel
            label={"Year"}
            name={"Billing year selection"}
            items={getYearSelectOptions(IMPACT_ORDER_SUPPORTED_FROM_YEAR)}
            value={String(year)}
            onChange={(event) => {
              setYear(event.target.value);
            }}
          />
        </SelectContainer>
      }
    >
      {!isApiHistoryLoading && data ? (
        <>
          <APIUsageGraph
            usageData={data.periods}
            currency={user.preferredCurrency}
          />
          <ChartCaption use="body3">
            Pending impact refers to API usage that has been requested but not
            yet funded. Your API usage is billed on the first day of each month,
            and the impact is applied to your profile in a pending state until
            receipt of payment.
          </ChartCaption>
        </>
      ) : (
        <LoadingContainer>
          <Spinner isLarge />
        </LoadingContainer>
      )}
    </StyledUsageSection>
  );
};
